import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import OsScroll from '../ui/OsScroll';

export const DROPDOWN_ROW_HEIGHT = 28;

/**
 * The ONE dropdown-list shell for the event editor's custom controls (owner,
 * 30.08: no browser-default dropdown anywhere in the editor; extracted rather
 * than duplicated once the time comboboxes and the selects shared it —
 * modular-files rule). Anchored portal, `cal-popover` chrome, `ctx-menu-btn`
 * rows, OsScroll list, opens scrolled to the selected row (rAF retry — the
 * DbDatePicker deferred-init pattern). Presentation only: the OWNER of the
 * trigger decides when it opens/closes and what a pick means.
 *
 * items: [{ key, label, hint?, selected?, onPick }]
 * id: the listbox's DOM id, so the trigger that owns it can point
 *   `aria-controls` at it (a combobox must name the popup it opens).
 */
export default function DropdownList({
  id,
  anchorRect,
  items,
  ariaLabel,
  onRequestClose,
  maxRows = 7,
  minWidth = 130,
}) {
  const { t } = useTranslation();
  const osRef = useRef(null);

  // Open at/near the selected row.
  useEffect(() => {
    let raf;
    let tries = 0;
    const scroll = () => {
      const viewport = osRef.current?.osInstance?.()?.elements?.().viewport;
      if (viewport) {
        const idx = Math.max(0, items.findIndex((i) => i.selected));
        viewport.scrollTop = Math.max(0, idx * DROPDOWN_ROW_HEIGHT - 3 * DROPDOWN_ROW_HEIGHT);
      } else if (tries++ < 10) {
        raf = requestAnimationFrame(scroll);
      }
    };
    raf = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outside mousedown closes (the trigger's own mousedown is the caller's job
  // to exempt — it usually re-toggles).
  useEffect(() => {
    const onDown = (e) => {
      if (e.target.closest?.('[data-editor-dropdown]')) return;
      onRequestClose?.();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onRequestClose]);

  if (!anchorRect) return null;

  return ReactDOM.createPortal(
    <div
      className="cal-popover"
      data-editor-dropdown="true"
      id={id}
      role="listbox"
      aria-label={ariaLabel}
      style={{
        top: Math.min(anchorRect.bottom + 4, (window.innerHeight || 800) - maxRows * DROPDOWN_ROW_HEIGHT - 40),
        left: anchorRect.left,
        width: Math.max(anchorRect.width, minWidth),
        padding: 4,
      }}
    >
      <OsScroll ref={osRef} style={{ maxHeight: maxRows * DROPDOWN_ROW_HEIGHT }}>
        <div>
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="option"
              aria-selected={!!item.selected}
              className="ctx-menu-btn"
              style={{ height: DROPDOWN_ROW_HEIGHT, width: '100%' }}
              // mousedown, not click: commits BEFORE a trigger input's blur.
              onMouseDown={(e) => {
                e.preventDefault();
                item.onPick();
              }}
            >
              <span>{item.label}</span>
              {item.hint != null && (
                <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 11 }}>
                  {item.hint}
                </span>
              )}
            </button>
          ))}
          {items.length === 0 && (
            <div style={{ padding: 6, fontSize: 12, color: '#9ca3af' }}>{t('calendar.noMatches')}</div>
          )}
        </div>
      </OsScroll>
    </div>,
    document.body
  );
}
